import React, { useEffect, useRef } from 'react';
import { applyServiceWorkerUpdate, hasServiceWorkerUpdate, SW_UPDATE_EVENT } from '../../serviceWorkerRegistration';
import { hasActiveLocalVisit, pendingOpCount } from '../../services/offline/offlineServices';
import { OUTBOX_CHANGED_EVENT } from '../../services/offline/outbox';
import { hasUnsavedChanges } from '../../hooks/useUnsavedChangesGuard';

/**
 * Silent app update (replaces the "A new version of pelora is ready" banner, 2026-10-29).
 *
 * Users never see or choose the app version. A new build installs in the background (service
 * worker, Phase 16A) and is applied with a reload at the first moment that cannot lose work:
 *   - nothing to lose: no unsynced offline work, no visit in progress on this device (design §6.1,
 *     plan C12), and no form with unsaved changes (useUnsavedChangesGuard), AND
 *   - a quiet moment: signed out (sign-in or lock screen), the first seconds after the page loaded
 *     before any input, or the tab is in the background and the user has been idle for a minute.
 * Until then the waiting build stays in the background; nothing is shown.
 */

/** Design §6.1 / plan C12: never mid-visit, never with unsynced work. */
export async function canApplyUpdateNow(): Promise<boolean> {
  return (await pendingOpCount()) === 0 && !(await hasActiveLocalVisit());
}

export const STARTUP_WINDOW_MS = 10_000;
export const BACKGROUND_IDLE_MS = 60_000;
export const CHECK_INTERVAL_MS = 30_000;

export interface QuietMomentInput {
  signedIn: boolean;
  hidden: boolean;
  /** ms since the last keyboard, pointer, touch or wheel input (Infinity when none yet) */
  idleMs: number;
  /** ms since the page loaded */
  sinceLoadMs: number;
  /** any input since the page loaded */
  inputSinceLoad: boolean;
}

/** A moment when a reload interrupts nobody. Pure. */
export function isQuietMoment({ signedIn, hidden, idleMs, sinceLoadMs, inputSinceLoad }: QuietMomentInput): boolean {
  if (!signedIn) return true;
  if (sinceLoadMs <= STARTUP_WINDOW_MS && !inputSinceLoad) return true;
  return hidden && idleMs >= BACKGROUND_IDLE_MS;
}

const INPUT_EVENTS = ['keydown', 'pointerdown', 'touchstart', 'wheel'] as const;

interface AppUpdaterProps {
  /** Signed in (online session or an unlocked offline session) */
  signedIn: boolean;
}

const AppUpdater: React.FC<AppUpdaterProps> = ({ signedIn }) => {
  const loadedAt = useRef(Date.now());
  const lastInput = useRef<number | null>(null);
  const signedInRef = useRef(signedIn);
  signedInRef.current = signedIn;
  const applying = useRef(false);
  const runRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    const onInput = () => { lastInput.current = Date.now(); };
    INPUT_EVENTS.forEach((e) => window.addEventListener(e, onInput, { passive: true, capture: true }));

    const check = async () => {
      if (applying.current || !hasServiceWorkerUpdate()) return;
      const now = Date.now();
      const quiet = isQuietMoment({
        signedIn: signedInRef.current,
        hidden: typeof document !== 'undefined' && document.visibilityState === 'hidden',
        idleMs: lastInput.current === null ? Infinity : now - lastInput.current,
        sinceLoadMs: now - loadedAt.current,
        inputSinceLoad: lastInput.current !== null,
      });
      if (!quiet || hasUnsavedChanges()) return;
      // One check at a time: the offline lookups are async and several triggers can fire together
      applying.current = true;
      let safe = false;
      try {
        safe = await canApplyUpdateNow();
      } catch {
        safe = false; // can't tell whether offline work is pending: wait
      }
      if (!safe || hasUnsavedChanges()) {
        applying.current = false;
        return;
      }
      applyServiceWorkerUpdate(); // reloads the page; `applying` stays set
    };
    const run = () => { check().catch(() => undefined); };
    runRef.current = run;

    window.addEventListener(SW_UPDATE_EVENT, run);
    window.addEventListener(OUTBOX_CHANGED_EVENT, run);
    document.addEventListener('visibilitychange', run);
    const timer = setInterval(run, CHECK_INTERVAL_MS);
    run();
    return () => {
      INPUT_EVENTS.forEach((e) => window.removeEventListener(e, onInput, { capture: true }));
      window.removeEventListener(SW_UPDATE_EVENT, run);
      window.removeEventListener(OUTBOX_CHANGED_EVENT, run);
      document.removeEventListener('visibilitychange', run);
      clearInterval(timer);
    };
  }, []);

  // Signing out (or the idle sign-out / lock) is a quiet moment
  useEffect(() => {
    if (!signedIn) runRef.current();
  }, [signedIn]);

  return null;
};

export default AppUpdater;
